// A small AM receiver: synthetic repeated captures, complex downconversion,
// a two-pole Butterworth low-pass filter, and envelope/ASCII decoding.
export const SAMPLE_RATE = 4096;
export const SAMPLES_PER_BIT = 64;
export const MISSIONS = [
  {id: "lunar", name: "Lunar relay", tag: "01 / FIRST CONTACT", source: "SELENE-7 · LUNAR ORBIT", frequency: 612, noise: 1.4, interference: .55, seed: 19, message: "MOON BASE ONLINE. SEND COFFEE."},
  {id: "mars", name: "Mars outpost", tag: "02 / DEEP SPACE", source: "ARES-3 · MARS SURFACE", frequency: 438, noise: 2.2, interference: .85, seed: 71, message: "ROVER SAFE. FOUND A ROCK. ANOTHER ROCK."},
  {id: "unknown", name: "Unknown origin", tag: "03 / UNIDENTIFIED", source: "UNKNOWN · OUTER SYSTEM", frequency: 826, noise: 2.3, interference: 1.1, seed: 113, message: "WE COME IN PEACE. YOUR WIFI IS TERRIBLE."}
];

export function messageBits(message) {
  return Array.from(message).reduce((bits, character) => {
    for (let shift = 7; shift >= 0; shift--) bits.push((character.charCodeAt(0) >> shift) & 1);
    return bits;
  }, []);
}

export function createTransmission(mission) {
  const bits = messageBits(mission.message);
  const length = (bits.length + 2) * SAMPLES_PER_BIT;
  const carrier = new Float32Array(length);
  for (let i = 0; i < length; i++) {
    const time = i / SAMPLE_RATE;
    const bit = bits[Math.floor(i / SAMPLES_PER_BIT) - 2] || 0;
    carrier[i] = (.1 + .9 * bit) * Math.cos(2 * Math.PI * mission.frequency * time)
      + mission.interference * Math.cos(2 * Math.PI * (mission.frequency + 160) * time)
      + .3 * Math.cos(2 * Math.PI * (mission.frequency - 200) * time);
  }
  const captures = Array.from({length: 8}, (_, capture) => {
    let seed = mission.seed + capture * 7919;
    const samples = new Float32Array(length);
    for (let i = 0; i < length; i++) {
      seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
      samples[i] = carrier[i] + (seed / 4294967296 * 2 - 1) * mission.noise;
    }
    return samples;
  });
  return {bits, captures};
}

function lowPass(cutoff) {
  const omega = 2 * Math.PI * cutoff / SAMPLE_RATE;
  const cosine = Math.cos(omega);
  const alpha = Math.sin(omega) / Math.sqrt(2);
  const a0 = 1 + alpha;
  const b0 = (1 - cosine) / (2 * a0);
  const a1 = -2 * cosine / a0;
  const a2 = (1 - alpha) / a0;
  let x1 = 0, x2 = 0, y1 = 0, y2 = 0;
  return sample => {
    const value = b0 * sample + 2 * b0 * x1 + b0 * x2 - a1 * y1 - a2 * y2;
    x2 = x1; x1 = sample; y2 = y1; y1 = value;
    return value;
  };
}

export function receiveTransmission(transmission, mission, frequency, bandwidth, averaging) {
  const filterI = lowPass(bandwidth / 2);
  const filterQ = lowPass(bandwidth / 2);
  const envelope = new Float32Array(transmission.captures[0].length);
  for (let i = 0; i < envelope.length; i++) {
    let sample = 0;
    for (let capture = 0; capture < averaging; capture++) sample += transmission.captures[capture][i];
    sample /= averaging;
    const phase = 2 * Math.PI * frequency * i / SAMPLE_RATE;
    const inPhase = filterI(sample * Math.cos(phase));
    const quadrature = filterQ(sample * Math.sin(phase));
    envelope[i] = Math.sqrt(inPhase * inPhase + quadrature * quadrature);
  }
  const decodedBits = transmission.bits.map((_, bit) => {
    // Sample late in each symbol to allow the low-pass filter to settle.
    const start = (bit + 2) * SAMPLES_PER_BIT + 40;
    let level = 0;
    for (let i = start; i < start + 20; i++) level += envelope[i];
    return level / 20 > .27 ? 1 : 0;
  });
  let correctBits = 0;
  decodedBits.forEach((bit, index) => { if (bit === transmission.bits[index]) correctBits++; });
  let recovered = 0;
  const text = Array.from(mission.message).map((character, index) => {
    let code = 0;
    for (let bit = 0; bit < 8; bit++) code = (code << 1) | decodedBits[index * 8 + bit];
    // This training receiver knows the source packet and checks each byte.
    if (code === character.charCodeAt(0)) { recovered++; return character; }
    return "·";
  }).join("");
  return {envelope, text, recovered, integrity: Math.floor(correctBits / decodedBits.length * 100), locked: recovered === mission.message.length};
}

export function spectrum(samples) {
  const size = 2048;
  const points = [];
  for (let frequency = 240; frequency <= 960; frequency += 4) {
    let real = 0, imaginary = 0;
    for (let i = 0; i < size; i++) {
      const window = .5 - .5 * Math.cos(2 * Math.PI * i / (size - 1));
      const phase = 2 * Math.PI * frequency * i / SAMPLE_RATE;
      real += samples[i] * window * Math.cos(phase);
      imaginary += samples[i] * window * Math.sin(phase);
    }
    points.push(Math.sqrt(real * real + imaginary * imaginary) / size);
  }
  const max = Math.max(...points, .01);
  return points.map(value => value / max);
}
